import type { Article } from "../../../types/blog";

const article: Article = {
  slug: "vitre-dos-telephone-casse",
  silo: "telephone",
  h1: "Vitre arrière du téléphone cassée : un problème plus sérieux qu'il n'y paraît",
  metaTitle: "Vitre arrière de téléphone cassée : que faire ?",
  metaDescription: "Dos du téléphone fissuré ou brisé après une chute ? Découvrez les risques réels, la différence avec la casse d'écran et les options de réparation.",
  excerpt: "On pense souvent que seul l'écran avant compte, mais une vitre arrière cassée expose aussi votre téléphone à des risques bien réels. Voici pourquoi il ne faut pas la négliger.",
  publishedAt: "2026-09-01",
  readingMinutes: 6,
  relatedSlugs: ["ecran-telephone-casse", "telephone-tombe-dans-eau", "batterie-telephone-se-decharge-vite", "telephone-qui-surchauffe"],
  faq: [
    {
      question: "Une vitre arrière cassée est-elle aussi grave qu'un écran cassé ?",
      answer: "C'est différent mais tout aussi sérieux à moyen terme. La vitre arrière protège la batterie et les composants internes de l'humidité et de la poussière, et sur de nombreux modèles récents elle sert aussi de plaque de recharge sans fil, ce qui rend sa casse plus qu'esthétique.",
    },
    {
      question: "Peut-on continuer à utiliser un téléphone avec le dos cassé ?",
      answer: "Cela reste possible à court terme si les éclats ne se détachent pas, mais les fissures s'agrandissent avec les manipulations et les infiltrations de poussière ou d'humidité deviennent de plus en plus probables avec le temps.",
    },
    {
      question: "La casse du dos du téléphone affecte-t-elle la charge sans fil ?",
      answer: "Sur les modèles compatibles avec la charge par induction, une vitre arrière très fissurée ou mal repositionnée après un choc peut perturber l'alignement de la bobine de charge et rendre la recharge sans fil moins fiable, voire inopérante.",
    },
    {
      question: "Faut-il réparer la vitre arrière même si elle ne gêne pas l'usage quotidien ?",
      answer: "Oui, il est recommandé de la faire réparer rapidement même si l'appareil semble fonctionner normalement, car la vitre arrière fait partie de l'étanchéité globale du téléphone et sa dégradation progressive augmente le risque de dommages internes.",
    },
  ],
  content: [
    {
      type: "p",
      text: "Quand on pense à un téléphone cassé, on imagine presque toujours un écran fissuré. Pourtant, la vitre arrière, celle qui recouvre le dos de l'appareil, casse tout aussi fréquemment lors d'une chute, et ses conséquences sont souvent sous-estimées par les utilisateurs.",
    },
    {
      type: "h2",
      text: "Pourquoi la vitre arrière casse aussi facilement",
    },
    {
      type: "p",
      text: "Sur la plupart des smartphones récents, le dos de l'appareil est en verre plutôt qu'en plastique ou en métal, pour des raisons esthétiques mais aussi pour permettre la charge sans fil. Ce choix de matériau rend l'arrière du téléphone presque aussi fragile que l'écran avant en cas de chute, surtout si l'appareil tombe à plat sur le dos ou sur un angle.",
    },
    {
      type: "ul",
      items: [
        "Chute à plat sur le dos, sur une surface dure comme du carrelage ou du béton",
        "Choc sur un coin ou une arête qui fissure le verre en étoile",
        "Pression excessive dans une poche serrée avec d'autres objets",
        "Vitre déjà fragilisée par une précédente réparation ou un précédent choc",
      ],
    },
    {
      type: "h2",
      text: "En quoi c'est différent d'un écran cassé",
    },
    {
      type: "p",
      text: "La casse de la vitre arrière n'affecte généralement pas l'utilisation immédiate du téléphone : pas de tactile concerné, pas d'affichage perturbé. C'est justement ce qui pousse beaucoup d'utilisateurs à repousser la réparation. Mais la vitre arrière joue un rôle de protection tout aussi important que l'écran, notamment pour l'étanchéité de l'appareil et la protection de la batterie logée juste en dessous.",
    },
    {
      type: "p",
      text: "Contrairement à un [écran cassé](slug:ecran-telephone-casse) qui gêne rapidement l'usage au quotidien, une vitre arrière fissurée peut passer inaperçue pendant des semaines, ce qui laisse le temps aux infiltrations de s'aggraver silencieusement.",
    },
    {
      type: "callout",
      variant: "warning",
      title: "Le risque invisible des infiltrations",
      text: "Une fissure au dos du téléphone peut laisser passer de l'humidité vers la batterie et les composants internes sans que cela soit visible de l'extérieur. Si le téléphone est ensuite exposé à la pluie ou à un environnement humide, le risque de dommage interne augmente fortement.",
    },
    {
      type: "h2",
      text: "Les risques concrets d'une vitre arrière cassée",
    },
    {
      type: "ul",
      items: [
        "Infiltration progressive de poussière et d'humidité vers la batterie et la carte mère",
        "Risque de coupure si des éclats de verre se détachent au niveau des bords",
        "Perturbation de la charge sans fil sur les modèles équipés de cette fonction",
        "Aggravation de la fissure à chaque nouvelle pression ou chute, même légère",
        "Perte d'étanchéité de l'appareil, notamment en cas de contact accidentel avec [l'eau](slug:telephone-tombe-dans-eau)",
      ],
    },
    {
      type: "h2",
      text: "Réparer la vitre arrière : ce qu'il faut savoir",
    },
    {
      type: "p",
      text: "Le remplacement de la vitre arrière est une intervention technique différente de celle d'un écran, car cette vitre est souvent collée directement au châssis du téléphone et à proximité immédiate de la batterie. Sur certains modèles, un démontage complet de l'appareil est nécessaire pour accéder à la vitre et la remplacer proprement.",
    },
    {
      type: "p",
      text: "Le coût et la complexité varient selon le modèle : certains téléphones ont une vitre arrière facilement remplaçable, d'autres nécessitent un démontage plus poussé qui augmente le temps d'intervention. Un diagnostic permet d'évaluer précisément l'étendue des dégâts, y compris de vérifier que la batterie n'a pas été touchée par le choc.",
    },
    {
      type: "callout",
      variant: "tip",
      title: "Profitez-en pour un contrôle complet",
      text: "Une chute qui casse la vitre arrière peut aussi avoir affecté d'autres éléments internes, comme la batterie ou les nappes de connexion. C'est l'occasion de faire vérifier l'ensemble de l'appareil plutôt que de réparer uniquement la vitre visible.",
    },
    {
      type: "h2",
      text: "Comment limiter le risque à l'avenir",
    },
    {
      type: "ol",
      items: [
        "Utiliser une coque intégrale qui protège aussi bien l'avant que l'arrière du téléphone",
        "Éviter de poser le téléphone à plat sur des surfaces dures sans protection",
        "Être particulièrement vigilant si le téléphone est équipé de la charge sans fil, car la coque doit rester compatible",
        "Faire réparer une vitre déjà fissurée rapidement, avant qu'un second choc n'aggrave les dégâts",
      ],
    },
    {
      type: "p",
      text: "Si votre téléphone a subi un choc qui a fissuré le dos, n'hésitez pas à le faire examiner rapidement, même si tout semble fonctionner normalement en surface. Nos techniciens vérifient systématiquement l'état de la batterie et de l'étanchéité avant et après remplacement de la vitre.",
    },
  ],
};

export default article;
